// Pure-function backoff + retry-cap + outcome-classification policy for
// the attachment re-download pipeline.
//
// Three independent decisions live here:
//   1. `nextEligibleAt`     — exponential-backoff schedule (1, 4, 16, 60,
//                              300, 300, 300, 300 seconds) capped at the
//                              eighth attempt.
//   2. `classifyOutcome`    — maps a network outcome to one of three effect
//                              classes: success, retryable (advance counter),
//                              permanent (jump to expired). Transient network
//                              errors are retryable but do NOT advance the
//                              counter.
//   3. `canStartNew`        — bandwidth ceiling: at most 4 in-flight
//                              re-downloads at a time so a screen full of old
//                              attachments cannot saturate the connection.
//
// All decisions are pure functions of their inputs so the self-test can
// exercise every (state × outcome) pair without touching disk or shared
// singletons.
//
// Web mirror of kaether_for_ios/Stoat/E2EE/AttachmentRedownloadPolicy.swift

import type { AttachmentRedownloadOutcome } from "./AttachmentRedownload";

/**
 * Collapsed effect class returned by `classifyOutcome`. The state machine
 * applies the class; this module is only the classifier.
 *
 * - `success`   — download completed; transition to `completed`.
 * - `retryable` — reschedule; whether the attempt counter advances is
 *                 determined by the originating outcome (transient → no,
 *                 HTTP retryable → yes) and applied by the state machine.
 * - `permanent` — server confirmed the blob is gone (HTTP 410) or the retry
 *                 budget is exhausted; transition straight to `expired`.
 */
export type RedownloadOutcomeClass = "success" | "retryable" | "permanent";

/** Sentinel representing "never retry again" (equivalent to iOS's distantFuture). */
export const DISTANT_FUTURE = Number.MAX_SAFE_INTEGER;

/**
 * Indexed by attempt number. `BACKOFF_SCHEDULE[N]` is the wait in **seconds**
 * before attempt N+1. Capped at 300 s for the tail entries so we keep trying
 * once-per-five-minutes indefinitely until `MAX_ATTEMPTS` is reached.
 *
 * Byte-for-byte mirror of iOS: [1, 4, 16, 60, 300, 300, 300, 300]
 */
export const BACKOFF_SCHEDULE: readonly number[] = [
  1, 4, 16, 60, 300, 300, 300, 300,
] as const;

/**
 * Hard ceiling on counted attempts. After this many HTTP-counted failures we
 * transition the record to `expired` even if the server only ever returned
 * soft errors.
 */
export const MAX_ATTEMPTS = 8;

/**
 * Maximum concurrent in-flight downloads. Prevents a screen full of old
 * attachments from saturating the user's connection.
 */
export const CONCURRENT_DOWNLOAD_CAP = 4;

/**
 * Earliest Unix timestamp (ms) at which we should attempt the next download.
 *
 * Once `attemptCount >= MAX_ATTEMPTS`, returns `DISTANT_FUTURE` — the caller
 * is expected to surface the record as `expired` rather than schedule another
 * attempt.
 *
 * @param attemptCount  The current `record.attemptCount` (after the failure
 *                      has been counted — i.e. the value that will be stored).
 * @param lastAttemptedAt  Unix timestamp (ms) stamped on the outcome.
 */
export function nextEligibleAt(
  attemptCount: number,
  lastAttemptedAt: number,
): number {
  if (attemptCount >= MAX_ATTEMPTS) {
    return DISTANT_FUTURE;
  }
  const idx = Math.min(attemptCount, BACKOFF_SCHEDULE.length - 1);
  const waitMs = BACKOFF_SCHEDULE[idx] * 1_000;
  return lastAttemptedAt + waitMs;
}

/**
 * Maps a network outcome to one of three effect classes. The state machine
 * is responsible for *applying* the class (counter advance, state transition);
 * this function is the classifier only.
 */
export function classifyOutcome(
  outcome: AttachmentRedownloadOutcome,
): RedownloadOutcomeClass {
  if (outcome.kind === "success") {
    return "success";
  }
  if (outcome.kind === "transientFailure") {
    return "retryable";
  }
  // permanentFailure
  const { httpStatus } = outcome;
  if (httpStatus === 410) return "permanent";
  if (httpStatus >= 200 && httpStatus < 300) return "success";
  // 404, 5xx, and anything else server-side is retryable.
  return "retryable";
}

/**
 * Bandwidth-cap gate. Caller passes the current count of `inFlight` records
 * and decides whether to start another one.
 */
export function canStartNew(currentInFlightCount: number): boolean {
  return currentInFlightCount < CONCURRENT_DOWNLOAD_CAP;
}
